/**
 * Connect your Shopify store (BLU-1376, D8; handover 4.4).
 *
 * Every org sees the install path here; the full Shopify card (credentials,
 * sync settings, webhooks, backfill) lives only in Settings, Integrations.
 * Blue Monk registers the store's custom app and pastes its install link from
 * the platform side, so nobody in the wizard sees a Client ID or Secret form
 * (G4):
 *  - connected (the `connected` prop: the REAL connection signal, domain and
 *    credentials, never the step's completion) → the green saved box
 *    "Connected to <shop>.". A manual Mark as done completes the step for
 *    progress but shows no saved box, and the Install link and its copy
 *    fallback stay until the store is really connected;
 *  - a link that passes `isShopifyInstallLink` (https on admin.shopify.com)
 *    → ONE primary "Install on Shopify" link in a new tab, plus the copy
 *    fallback;
 *  - anything else (no app, no link, a foreign link) → the waiting text.
 *
 * The read is the org-scope `getConnectionStatus` (no client id, no secret),
 * on the same cache key as `useShopifyConnection`, so it never doubles a
 * request. A platform admin also gets the platform `ShopifyConnectionSection`
 * (register the app, the install link editor, Start syncing), wrapped under a
 * small label. Sync stays a manual platform action (Q4): nothing here starts it.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, ExternalLink } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';
import { ShopifyConnectionSection } from '@/features/organization/components/ShopifyConnectionSection';
import { CopyValue } from '@/features/organization/components/shopify-connection/CopyValue';
import { isShopifyInstallLink } from '@/features/organization/components/shopify-connection/presentation';
import { StepPane } from '../shell/StepPane';
import { PaneActionButton } from '../shell/PaneActionButton';

interface StepShopifyProps {
  /** The store is really connected (domain + credentials). Never the step's completion. */
  connected: boolean;
  organizationId: string;
}

export function StepShopify({ connected, organizationId }: StepShopifyProps) {
  const { t } = useTranslation('onboarding');
  const { isPlatformAdmin } = useRole();
  const status = trpc.shopifyIntegration.getConnectionStatus.useQuery(undefined, { refetchOnWindowFocus: true });

  const customApp = status.data?.customApp ?? null;
  const installLink = customApp?.installLink ?? null;
  const linkReady = !connected && installLink != null && isShopifyInstallLink(installLink);

  // The saved box only when really connected (a manual Mark as done is not).
  const summary = !connected
    ? undefined
    : customApp?.shop
      ? t('stepShopify.connected', { shop: customApp.shop })
      : t('stepShopify.connectedNoShop');

  const installButton =
    linkReady && installLink ? (
      <PaneActionButton asChild>
        <a href={installLink} target="_blank" rel="noopener noreferrer">
          {t('stepShopify.install')}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      </PaneActionButton>
    ) : undefined;

  let body: ReactNode = null;
  if (connected) {
    // The saved box says it all.
  } else if (status.isLoading) {
    body = <Skeleton className="h-10 w-full max-w-md" />;
  } else if (linkReady && installLink) {
    body = (
      <div className="max-w-prose space-y-4">
        <p className="text-sm text-foreground">{t('stepShopify.installBody')}</p>
        <CopyValue
          label={t('stepShopify.linkLabel')}
          value={installLink}
          hint={<p className="text-xs text-muted-foreground">{t('stepShopify.copyHint')}</p>}
        />
      </div>
    );
  } else {
    body = (
      <div className="flex max-w-prose items-start gap-2.5 rounded-lg bg-muted/60 px-4 py-3">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">{t('stepShopify.waiting')}</p>
      </div>
    );
  }

  const platformSection = isPlatformAdmin() ? (
    <div className="mt-6 border-t border-dashed border-line-strong pt-4">
      <p className="mb-3 text-[11px] font-semibold uppercase leading-4 tracking-wide text-muted-foreground">
        {t('stepShopify.platformOnly')}
      </p>
      <ShopifyConnectionSection organizationId={organizationId} mode="platform" />
    </div>
  ) : null;

  return (
    <StepPane
      title={t('stepShopify.title')}
      description={t('stepShopify.description')}
      summary={summary}
      primary={installButton}
    >
      {body == null && platformSection == null ? undefined : (
        <>
          {body}
          {platformSection}
        </>
      )}
    </StepPane>
  );
}
